
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';

interface AchievementPopupProps {
  achievement: {
    name: string;
    description: string;
    icon: string;
    xp: number;
  } | null;
  onClose: () => void;
}

export const AchievementPopup: React.FC<AchievementPopupProps> = ({ achievement, onClose }) => {
  useEffect(() => {
    if (achievement) {
      // Trigger confetti
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#C5A059', '#f59e0b', '#ef4444'],
        zIndex: 100
      });
      
      // Auto-close after 5 seconds
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [achievement, onClose]);
  
  return (
    <AnimatePresence>
      {achievement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.5, rotate: -10, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            exit={{ scale: 0.5, rotate: 10, opacity: 0 }}
            transition={{ type: "spring", duration: 0.6, bounce: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-[#1c1917] to-stone-900 border-2 border-[#C5A059] p-12 rounded-3xl text-center max-w-md w-full shadow-[0_0_50px_rgba(197,160,89,0.3)] relative overflow-hidden"
          >
            {/* Animated Background */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
              className="absolute inset-[-50%] opacity-10"
              style={{
                backgroundImage: `radial-gradient(circle, #C5A059 2px, transparent 2px)`,
                backgroundSize: '30px 30px'
              }}
            />
            
            <div className="relative z-10">
              <Trophy className="w-20 h-20 mx-auto mb-6 text-[#C5A059] drop-shadow-[0_0_15px_rgba(197,160,89,0.5)]" />
              
              <h2 className="text-3xl font-black text-white mb-2 tracking-tight">
                ACHIEVEMENT UNLOCKED!
              </h2>
              
              <div className="text-6xl mb-6 scale-125 inline-block p-4">{achievement.icon}</div>
              
              <p className="text-2xl font-bold text-[#C5A059] mb-2 uppercase tracking-wide">
                {achievement.name}
              </p>
              
              <p className="text-stone-400 mb-8 font-mono text-sm leading-relaxed">
                {achievement.description}
              </p>
              
              <div className="bg-[#C5A059] text-stone-900 rounded-xl py-3 px-8 inline-block font-black text-xl shadow-lg transform hover:scale-105 transition-transform">
                +{achievement.xp} XP
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
